import { useState, useCallback, useRef } from 'react'
import { toolDefinitions, executeToolCall } from './menuTools'
import buildMenuSummary from './buildMenuSummary'

const API_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY || ''
const MODEL = 'claude-sonnet-4-20250514'
const MAX_TOKENS = 4096
const MAX_TOOL_ROUNDS = 10

function buildSystemPrompt(menuSummary) {
  return `You are a restaurant menu editor assistant. You help restaurant owners edit their menu by understanding their natural language requests and translating them into precise database operations.

RULES — follow these strictly:
1. NEVER guess or assume. If a request is ambiguous, ask clarifying questions.
2. Always show your understanding of the request before proposing changes.
3. Use your tools to look up specific menu items before making any claims about what exists.
4. When you have enough information, call propose_operations to present a structured change list.
5. Each operation must reference real document IDs from tool results (except for CREATE operations which generate new docs).
6. When the user says "delete" an item, default to setting draft=true (soft delete / hide) unless they explicitly say "permanently delete." Explain this to them.
7. Keep responses concise and focused.
8. To create a new product, use CREATE_PRODUCT. Set subMenuId to the target submenu ID and include productData with at minimum { name: { en: "..." }, price: "..." }. New products default to visible (draft=false). First use list_submenus or search_menu to find the right submenu.
9. To create a new submenu/category, use CREATE_SUBMENU. Include subMenuData with at minimum { name: { en: "..." } }. Optionally set section ("Food", "Drinks", "Desserts", or "Catering"). New submenus default to visible (draft=false).
10. To reorder items in an array (e.g. move a product within a category's orderIndex, or move a category within the shop's subMenusOrderIndex), use MOVE_IN_ORDER with docId (the doc containing the array), arrayField, moveId, and toIndex (0-based position).
11. When creating multiple products in a new submenu, first propose a CREATE_SUBMENU, then propose CREATE_PRODUCT operations with subMenuId set to "NEW_SUBMENU" — the system will wire them together automatically.
12. When adding modifiers to a newly created product, use UPDATE_MODIFIER operations with docId set to "NEW_PRODUCT" — the system will wire them to the product created by the preceding CREATE_PRODUCT automatically.

CURRENT MENU STRUCTURE:
${menuSummary}

Use the search_menu tool to find items by name, and get_product_details / get_submenu_details for full information before proposing changes.`
}

async function callClaude(messages, systemPrompt) {
  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'x-api-key': API_KEY,
      'anthropic-version': '2023-06-01',
      'content-type': 'application/json',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: MAX_TOKENS,
      system: systemPrompt,
      messages,
      tools: toolDefinitions,
    }),
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(`Claude API error ${res.status}: ${text}`)
  }

  return res.json()
}

/**
 * Core hook for the ChatCMS feature.
 *
 * Returns conversation messages (for display), loading state,
 * pending operations (when Claude proposes changes), and action handlers.
 */
export default function useMenuChat(shop) {
  // Display messages: { role: 'user'|'assistant', text: string, operations?: array }
  const [displayMessages, setDisplayMessages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pendingProposal, setPendingProposal] = useState(null)

  // Full Claude conversation history (includes tool_use / tool_result turns)
  const conversationRef = useRef([])

  const sendMessage = useCallback(async (userText) => {
    if (!API_KEY) {
      setError('Missing REACT_APP_ANTHROPIC_API_KEY environment variable.')
      return
    }

    setIsLoading(true)
    setError(null)

    // Add user message to display and conversation
    setDisplayMessages(prev => [...prev, { role: 'user', text: userText }])
    conversationRef.current = [
      ...conversationRef.current,
      { role: 'user', content: [{ type: 'text', text: userText }] },
    ]

    const systemPrompt = buildSystemPrompt(buildMenuSummary(shop))

    try {
      let rounds = 0

      while (rounds < MAX_TOOL_ROUNDS) {
        rounds++
        const response = await callClaude(conversationRef.current, systemPrompt)

        // Check for tool_use blocks
        const toolUseBlocks = (response.content || []).filter(b => b.type === 'tool_use')
        const textBlocks = (response.content || []).filter(b => b.type === 'text')

        // Append the full assistant response to conversation history
        conversationRef.current = [
          ...conversationRef.current,
          { role: 'assistant', content: response.content },
        ]

        // Handle propose_operations specially
        const proposal = toolUseBlocks.find(b => b.name === 'propose_operations')
        if (proposal) {
          const { understanding, operations } = proposal.input
          setPendingProposal({ understanding, operations })
          setDisplayMessages(prev => [
            ...prev,
            {
              role: 'assistant',
              text: understanding,
              operations,
              toolUseId: proposal.id,
            },
          ])

          // Send tool_result to keep conversation valid
          conversationRef.current = [
            ...conversationRef.current,
            {
              role: 'user',
              content: [{
                type: 'tool_result',
                tool_use_id: proposal.id,
                content: 'Operations displayed to user for approval. Waiting for user decision.',
              }],
            },
          ]

          setIsLoading(false)
          return
        }

        // If there are non-proposal tool calls, execute them and loop
        if (toolUseBlocks.length > 0) {
          const toolResults = toolUseBlocks.map(block => {
            const result = executeToolCall(shop, block.name, block.input)
            return {
              type: 'tool_result',
              tool_use_id: block.id,
              content: JSON.stringify(result),
            }
          })

          conversationRef.current = [
            ...conversationRef.current,
            { role: 'user', content: toolResults },
          ]

          // If there was also text alongside tools, don't display it yet —
          // wait for the final text-only response after tool loop completes.
          continue
        }

        // No tool calls — this is the final text response
        const text = textBlocks.map(b => b.text).join('\n\n')
        if (text) {
          setDisplayMessages(prev => [...prev, { role: 'assistant', text }])
        }

        setIsLoading(false)
        return
      }

      setError('Too many tool call rounds. Please try rephrasing your request.')
      setIsLoading(false)
    } catch (err) {
      console.error('Claude API error:', err)
      setError(err.message)
      setIsLoading(false)
    }
  }, [shop])

  const clearProposal = useCallback(() => {
    setPendingProposal(null)
  }, [])

  const resetChat = useCallback(() => {
    setDisplayMessages([])
    setPendingProposal(null)
    setError(null)
    conversationRef.current = []
  }, [])

  return {
    displayMessages,
    isLoading,
    error,
    pendingProposal,
    sendMessage,
    clearProposal,
    resetChat,
  }
}
